import { GoogleGenerativeAI } from '@google/generative-ai';

const SYSTEM_INSTRUCTION = `
You are an intake assistant for System Audit, an independent investigative research organization.

Your only job is to:
- Help the user tell a factual, chronological account of events.
- Ask clarifying questions that improve evidential value (who, what, when, where, how, who witnessed it, what records exist).
- Highlight potential evidence (documents, messages, recordings, medical records, financial records) and ask whether the user has them.
- Extract and summarize metadata: dates, locations, people/organizations mentioned, communication channels, document types.
- Flag immediate safety concerns or imminent risk of harm in a neutral way.

You must NOT:
- Give legal advice.
- Tell the user what to do next beyond "System Audit will review this and may contact you."
- Promise outcomes, investigations, or publication.
- Encourage or assist any illegal activity.

Always:
- Keep the user's own words where possible.
- Ask one focused question at a time.
- Distinguish clearly between what the user states as fact and what appears as inference or opinion.
- Be concise but thorough.

When the user seems to have finished their story, provide a final synthesis of the key facts.
`;

export class IntakeChat {
  private chat: any;
  private genAI: GoogleGenerativeAI;

  constructor(apiKey: string) {
    this.genAI = new GoogleGenerativeAI(apiKey);
    const model = this.genAI.getGenerativeModel({ 
      model: 'gemini-1.5-flash',
      systemInstruction: SYSTEM_INSTRUCTION,
    });
    this.chat = model.startChat({
      generationConfig: {
        temperature: 0.7,
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 2048,
      },
    });
  }

  async sendMessage(message: string): Promise<string> {
    const result = await this.chat.sendMessage(message);
    const response = await result.response;
    return response.text();
  }
}

export const extractMetadata = async (apiKey: string, text: string) => {
  const genAI = new GoogleGenerativeAI(apiKey);
  const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
  
  const prompt = `Extract metadata from the following narrative and return ONLY valid JSON with this structure:
{
  "dates": ["array of dates mentioned"],
  "entities": ["array of people, organizations, locations mentioned"],
  "riskLevel": "low" | "medium" | "high",
  "summary": "brief summary of the narrative"
}

Narrative: ${text}`;

  const result = await model.generateContent(prompt);
  const response = await result.response;
  const responseText = response.text();
  
  // Extract JSON from response
  const jsonMatch = responseText.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    return JSON.parse(jsonMatch[0]);
  }
  
  throw new Error('Failed to extract metadata');
};
